import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { queryKeys } from "@/lib/queryKeys";

export type PayoutStatusState = "not_started" | "pending" | "complete";

export interface PayoutStatusRow {
  account_id: string;
  account_type: "express" | "standard";
  onboarding_status: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
}

/** Reads stripe_connect_accounts for the current user. State: complete if payouts_enabled, pending if row exists but not enabled, not_started if no row. */
export function usePayoutStatus() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: queryKeys.user.payoutStatus(user?.id ?? ""),
    queryFn: async (): Promise<{
      state: PayoutStatusState;
      row: PayoutStatusRow | null;
    }> => {
      if (!user?.id) return { state: "not_started", row: null };
      const { data, error } = await supabase
        .from("stripe_connect_accounts")
        .select("account_id, account_type, onboarding_status, charges_enabled, payouts_enabled, details_submitted")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      const r = data as (PayoutStatusRow | null) | undefined;
      const row = r ? { ...r, account_type: (r.account_type ?? "express") as "express" | "standard" } : null;
      if (!row) return { state: "not_started", row: null };
      const state: PayoutStatusState =
        row.payouts_enabled === true ? "complete" : "pending";
      return { state, row };
    },
    enabled: !!user?.id,
  });

  const refetch = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.user.payoutStatus(user?.id ?? "") });

  return {
    state: query.data?.state ?? "not_started",
    row: query.data?.row ?? null,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
    invalidate: refetch,
  };
}
